import { Button, Loader, Title } from "@mantine/core";
import { IconRefresh, IconShieldCheck } from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import { EmptyState } from "#/components/ui/EmptyState";
import { useClient } from "#/hook/useClient";
import { useDataHealth } from "#/hook/useDataHealth";
import { usePrvTranslation } from "#/pages/ClientDetail/components/prv/usePrvTranslation";
import { HEALTH_IMPACTS, type HealthImpact } from "#/types/dataHealth";
import { formatDateTime } from "#/utils/dateTimeUtils";
import HealthCheckCard from "./components/HealthCheckCard";
import HealthSummaryTiles from "./components/HealthSummaryTiles";
import PassingChecks from "./components/PassingChecks";
import { IMPACT_THEME } from "./healthCatalog";

/**
 * Data Health (own menu entry, client users). The gaps in the client's own
 * data that block the PRV engine or the Logistics Prediction today, grouped
 * by what they break, each record one click from the page that fixes it.
 * Read live. TMA account settings (status types) are not here: the admin
 * sees those on Client Detail.
 */
const DataHealthPage = () => {
  const t = usePrvTranslation();
  const { client } = useClient();
  const [selected, setSelected] = useState<HealthImpact | null>(null);

  const { data: report, isLoading, isError, isFetching, refetch } = useDataHealth();

  // A fresh look on every open: the badge may be up to five minutes old.
  useEffect(() => {
    void refetch();
  }, [refetch]);

  const sections = useMemo(
    () =>
      HEALTH_IMPACTS.filter((impact) => selected === null || impact === selected)
        .map((impact) => ({
          impact,
          checks: report?.checks.filter((check) => check.impact === impact) ?? [],
        }))
        .filter((section) => section.checks.length > 0),
    [report, selected]
  );

  return (
    <div className="flex w-full flex-col gap-5 pb-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <Title order={2}>{t("dataHealth.title")}</Title>
          <span className="text-sm text-gray-600">{t("dataHealth.subtitle")}</span>
          {report && (
            <span className="text-xs text-gray-500">
              {t("dataHealth.checkedAt", { time: formatDateTime(report.checkedAt, client) })}
              {report.openCount > 0 && (
                <>
                  {" · "}
                  <span className="font-medium text-gray-700">
                    {t("dataHealth.open", { count: report.openCount })}
                  </span>
                </>
              )}
            </span>
          )}
        </div>
        <Button
          variant="default"
          size="xs"
          leftSection={<IconRefresh size={14} />}
          loading={isFetching}
          onClick={() => void refetch()}
        >
          {t("dataHealth.refresh")}
        </Button>
      </div>

      {isLoading && (
        <div className="flex h-40 items-center justify-center">
          <Loader color="var(--client-color)" />
        </div>
      )}
      {isError && <EmptyState title={t("common.failedToProcess")} />}

      {report && report.openCount === 0 && (
        <div className="flex items-center gap-4 rounded-xl border border-emerald-200 bg-emerald-50 p-5">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
            <IconShieldCheck size={26} stroke={1.8} />
          </span>
          <span className="flex flex-col gap-0.5">
            <span className="text-base font-semibold text-emerald-900">
              {t("dataHealth.allClear.title")}
            </span>
            <span className="text-sm text-emerald-800">{t("dataHealth.allClear.description")}</span>
          </span>
        </div>
      )}

      {report && report.openCount > 0 && (
        <>
          <HealthSummaryTiles report={report} selected={selected} onSelect={setSelected} />
          {selected && <span className="text-xs text-gray-500">{t("dataHealth.filterHint")}</span>}
          {sections.map(({ impact, checks }) => {
            const theme = IMPACT_THEME[impact];
            const Icon = theme.icon;
            return (
              <section key={impact} className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <span
                    className={`flex h-7 w-7 items-center justify-center rounded-full ${theme.iconWrap}`}
                  >
                    <Icon size={16} stroke={1.8} />
                  </span>
                  <h2 className="text-sm font-semibold tracking-wide text-gray-800 uppercase">
                    {t(`dataHealth.impact.${impact}.title`)}
                  </h2>
                </div>
                {checks.map((check) => (
                  <HealthCheckCard key={check.key} check={check} />
                ))}
              </section>
            );
          })}
        </>
      )}

      {report && <PassingChecks keys={report.passing} />}
    </div>
  );
};

export default DataHealthPage;
